import { AnswerBlock, DigitalToolsUser } from "@/types";

export enum AssessmentRunStatus {
  NotStarted = 0,
  InProgress,
  Finished,
  Cancelled,
  NoAccess,
}

export enum AssessmentQuestionType {
  MultipleChoice = 0,
}

export interface AssessmentNodeTransitions {
  [transitionKey: string]: string;
}

export interface AssessmentNode {
  type: string;
}

export interface AssessmentNodeContentBlock {
  type: string;
}

export interface AssessmentNodeContentBlockText
  extends AssessmentNodeContentBlock {
  type: string;
  text: string;
  userStyle?: Record<string, string | Record<string, string>>;
}

export interface AssessmentNodeContentBlockImage
  extends AssessmentNodeContentBlock {
  type: "image";
  altText?: string;
  filestackHandle: string;
  userStyle?: Record<string, string | Record<string, string>>;
}

export interface AssessmentNodeContentBlockAudio
  extends AssessmentNodeContentBlock {
  type: "audio";
  filestackHandle: string;
  userStyle?: Record<string, string | Record<string, string>>;
}

export interface AssessmentNodeTitleCard extends AssessmentNode {
  type: "TitleCard";
  content: AssessmentNodeContentBlock[];
}

export interface AssessmentNodeMultipleChoiceScoring {
  pointsIfRight: number;
  pointsIfWrong: number;
}

export enum AssessmentNodeMultipleChoiceLayout {
  Default = "Default",
  SideBySide = "SideBySide",
}

export enum AssessmentNodeMultipleChoiceAnswerType {
  SingleSelect = "SingleSelect",
  MultiSelect = "MultiSelect",
  Poll = "Poll",
  TrueFalse = "TrueFalse",
}

export interface AssessmentNodeMultipleChoice extends AssessmentNode {
  type: "MultipleChoice";
  content: AssessmentNodeContentBlock[];
  answers: AssessmentNodeMultipleChoiceAnswer[];
  answersCount: number;
  learningStandards?: string[];
  scoring?: AssessmentNodeMultipleChoiceScoring;
  questionIndex: number;
  questionId: string;
  multiselect?: boolean;
  layout?: AssessmentNodeMultipleChoiceLayout;
  answerType?: AssessmentNodeMultipleChoiceAnswerType;
}

export interface AssessmentNodeMultipleChoiceAnswer {
  text?: string;
  imageHandle?: string;
  caption?: string;
  correct?: boolean;
  audioHandle?: string;
  userStyle?: Record<string, string | Record<string, string>>;
}

export interface AssessmentScriptMetadata {
  title: string;
  author?: string;
  description?: string;
  gradeLevels?: string[];
  learningStandards?: string[];
}

export interface AssessmentScriptScoring {
  type: string;
  pointsIfRight: number;
  pointsIfWrong: number;
}

export interface AssessmentScriptDefaultRules {
  scoring: AssessmentScriptScoring;
  timerEnabled: boolean;
}

export interface AssessmentScriptSection {
  sectionMetadata?: AssessmentScriptMetadata;
  defaultRules?: AssessmentScriptDefaultRules;
  nodes: AssessmentNode[];
}

export enum AssessmentVersionStatus {
  Draft = "Draft",
  Active = "Active",
}

export interface AssessmentScript {
  schemaVersion: number;
  assessmentMetadata: AssessmentScriptMetadata;
  sections: AssessmentScriptSection[];
}

export interface AssessmentRunScriptState {
  assessmentMetadata: AssessmentScriptMetadata;
  section: AssessmentScriptSection;
}

export interface TextAnswerBlock {
  text: string;
}
export interface ImageAnswerBlock {
  imageHandle: string;
  text: string;
  caption?: string;
}
export interface MediaAnswerBlock {
  imageHandle: string;
  audioHandle: string;
  text: string;
  caption?: string;
}

export interface AssessmentRunAnswer {
  questionId: string;
  questionIndex: number;
  correct: boolean;
  answersIndex: number[];
  answers: AnswerBlock[];
}

export interface AssessmentRunState {
  sectionIndex: number;
  nodeIndex: number;
  node?: AssessmentNode;
  status: AssessmentRunStatus;
  score: number;
  answers: AssessmentRunAnswer[];
}

export interface AssessmentRunServerState {
  sectionIndex: number;
  nodeIndex: number;
  node?: AssessmentNode;
  status: AssessmentRunStatus;
  score: number;
  answers?: AssessmentRunAnswer[];
}

export interface AssessmentRunRules {
  scoring: AssessmentScriptScoring;
}

export interface ResultsStudentState {
  user?: Pick<DigitalToolsUser, "usernameSecure">;
}

export interface ResultsAssessmentRunState {
  status: AssessmentRunStatus;
  score?: number;
  answers: AssessmentRunAnswer[];
}

export interface AssessmentRunResults {
  id: string;
  student?: ResultsStudentState;
  state: ResultsAssessmentRunState;
  parsedScript?: ParsedScript;
  updatedAt?: string;
}

export interface AssessmentQuestion {
  questionId: string;
  questionIndex: number;
  nodeIndex?: number;
  questionShortText: string;
  answer?: string;
  answers?: string[];
  questionType?: AssessmentQuestionType;
  questionNode?: AssessmentNodeMultipleChoice;
}

export type AssessmentQuestions = Record<string, AssessmentQuestion>;
export interface ParsedScript {
  questions?: AssessmentQuestions;
  cardCount?: number;
}

export interface Script {
  rawScript: AssessmentScript;
  cardCount?: number;
  questions?: AssessmentQuestions;
}

export type FormattedAssignmentSettings = {
  InstantFeedback: boolean;
  ShuffleAnswers: boolean;
  ShuffleQuestions: boolean;
};

// export enum SettingName {
//   InstantFeedback = "InstantFeedback",
// }
export type SettingName = keyof FormattedAssignmentSettings;
